// Marketo textarea fields are styled as single 48px lines (the shared h-12
// rules), but their placeholder can be longer than one line, e.g. the demo
// form's "Use Case" hint. textarea has no native text-overflow ellipsis
// support (inputs do), so the overflow is clipped mid-word instead of
// ellipsized. This measures each textarea placeholder against the field's
// inner width and, when it cannot fit, rewrites the attribute to a truncated
// version ending in "…". The full text is kept in data-full-placeholder so
// the measurement can re-run losslessly after web fonts load or the viewport
// resizes (both change how much text fits).

const measureCtx = () => {
  const canvas = document.createElement('canvas');

  return canvas.getContext('2d');
};

const fitPlaceholder = (textarea: HTMLTextAreaElement, ctx: CanvasRenderingContext2D) => {
  const full = textarea.dataset.fullPlaceholder ?? textarea.placeholder;
  if (!full) {
    return;
  }
  textarea.dataset.fullPlaceholder = full;

  const style = window.getComputedStyle(textarea);
  ctx.font = `${style.fontStyle} ${style.fontWeight} ${style.fontSize} ${style.fontFamily}`;

  const available = textarea.clientWidth - parseFloat(style.paddingLeft) - parseFloat(style.paddingRight);
  if (available <= 0 || ctx.measureText(full).width <= available) {
    textarea.placeholder = full;

    return;
  }

  let text = full;
  while (text.length > 0 && ctx.measureText(`${text.trimEnd()}…`).width > available) {
    text = text.slice(0, -1);
  }
  textarea.placeholder = `${text.trimEnd()}…`;
};

// eslint-disable-next-line @typescript-eslint/no-explicit-any
const truncatePlaceholders = (marketoForm: any) => {
  const formEl = marketoForm.getFormElem()[0] as HTMLElement | undefined;
  if (!formEl) {
    return;
  }

  const textareas = Array.from(formEl.querySelectorAll<HTMLTextAreaElement>('textarea[placeholder]'));
  if (!textareas.length) {
    return;
  }

  const ctx = measureCtx();
  if (!ctx) {
    return;
  }

  const run = () => {
    textareas.forEach(textarea => {
      fitPlaceholder(textarea, ctx);
    });
  };

  // The form is injected before layout settles; measure on the next frame.
  window.requestAnimationFrame(run);

  // Re-measure once the real web font is in: fallback-font metrics can be
  // narrower or wider than Roboto's, and the truncation point moves with them.
  document.fonts?.ready?.then(() => {
    window.requestAnimationFrame(run);
  });

  let resizeHandle: number | undefined;
  window.addEventListener('resize', () => {
    window.clearTimeout(resizeHandle);
    resizeHandle = window.setTimeout(run, 150);
  });
};

export default truncatePlaceholders;
